<template>
    <main
        v-if="data"
        class="relative pb-10 pt-25 md:pb-15 md:pt-36">
        <TheBreadcrumbs :items="breadcrumbs" />

        <section
            class="container relative mt-5 flex flex-col justify-between gap-5 overflow-hidden rounded-20 p-3 md:flex-row md:gap-10 md:p-5">
            <div class="relative z-20 grow">
                <AtomTitle
                    level="1"
                    class="!text-20/120 md:!text-24/133 lg:!text-32/100">
                    {{ data?.data.name }}
                </AtomTitle>
                <div
                    v-if="data.data.preview_description"
                    v-html="data.data.preview_description"
                    class="tinyeditor mt-3 text-16/148 font-medium text-accent-100 md:mt-5" />
            </div>

            <picture
                class="relative z-20 aspect-video w-full grow object-cover md:max-w-[50%]">
                <source
                    v-if="data.data.image?.[0].webp_url"
                    type="image/webp"
                    :srcset="
                        getMediaLink(
                            data.data.image?.[0].webp_url || '',
                            800
                        ) || ''
                    " />

                <source
                    v-else-if="
                        !data.data.image?.[0].webp_url &&
                        !data.data.image?.[0].url
                    "
                    type="image/webp"
                    srcset="/assets/images/ui/placeholder/webp/cromi.webp" />

                <img
                    :alt="
                        data.data.image?.[0].alt ||
                        data.data.name ||
                        'image not found'
                    "
                    :src="
                        getMediaLink(data.data.image?.[0].url || '', 800) ||
                        '/assets/images/ui/placeholder/jpg-png/cromi.png'
                    "
                    class="aspect-video w-full rounded-12 object-cover" />
            </picture>

            <div class="absolute inset-0 z-10 size-full">
                <div class="bg-noise"></div>
            </div>
            <div
                class="absolute -bottom-[10rem] -left-[5rem] z-10 aspect-square w-[20rem] animate-slow-pulse rounded-full bg-accent blur-[6rem] delay-300" />
            <div
                class="absolute -right-[5rem] -top-[10rem] z-10 aspect-square w-[20rem] animate-slow-pulse rounded-full bg-accent blur-[6rem] delay-500" />
            <div
                class="absolute -bottom-[5rem] left-1/2 z-10 aspect-square w-[10rem] -translate-x-1/2 animate-slow-pulse rounded-full bg-accent blur-[6rem] delay-500" />
        </section>

        <section
            v-if="data.data.blocks && data.data.blocks.data.length"
            class="container mt-10 space-y-10 md:mt-15 md:space-y-15">
            <BlockCard
                v-for="block in data.data.blocks.data"
                :key="block.id"
                :block-id="block.id"
                :block-type="blockTypes[block.type_id]"
                :text="block.text"
                :image="block.image ? block.image[0] : null"
                :gallery-images="block.image ? block.image : []"
                :title="block.title"
                :autoplay-video="block.video ? block.video[0] : null"
                :link-rutube="block.link_rutube"
                :preview-rutube="
                    block.preview_video ? block.preview_video[0] : null
                " />
            <AtomHr class="!mt-10 md:!mt-20" />
        </section>

        <PortfolioProducts
            v-if="data.data.products && data.data.products.data.length"
            :title="data.data.recommended_title || 'Наша продукция'"
            :items="data.data.products.data"
            class="py-10 md:py-20" />
        <AtomHr />

        <template v-if="hasCalculator(directionSlug) && calculator">
            <LazyVisibleCalculatorSection
                :direction-slug="directionSlug"
                :calculator="calculator"
                class="py-10 md:py-20" />
            <AtomHr />
        </template>

        <NewsSection
            v-if="data.data.read_also && data.data.read_also.data.length"
            title="Читайте также"
            hide-news-button
            hide-date
            :card-type="'article'"
            :items="data.data.read_also.data"
            class="py-10 md:py-20" />
    </main>
</template>

<script setup lang="ts">
import { getCalculator } from '~/services/calculator.service'
import type { ICalculator } from '~/types/calculator.types'
import type { TDirectionSlug } from '~/types/global.types'

import AtomHr from '~/components/atom/atom-hr/AtomHr.vue'
import AtomTitle from '~/components/atom/atom-title/AtomTitle.vue'
import TheBreadcrumbs, {
    type BreadcrumbItem,
} from '~/components/base/the-breadcrumbs/TheBreadcrumbs.vue'
import PortfolioProducts from '~/components/pages/portfolio/portfolio-products/PortfolioProducts.vue'
import BlockCard from '~/components/particles/block-card/BlockCard.vue'
import type { TBlockType } from '~/components/particles/block-card/block-card.types'
import NewsSection from '~/components/particles/news-section/NewsSection.vue'
import type { IArticleItem } from '~/components/particles/news-section/news-section.types'

const nuxtApp = useNuxtApp()
const modalStore = useModalStore()
const route = useRoute()

const LazyVisibleCalculatorSection = defineLazyHydrationComponent(
    'visible',
    () =>
        import(
            '~/components/particles/calculator-section/CalculatorSection.vue'
        )
)

const { data, error } = await useApiFetch<Include<IArticleItem>>(
    `/v1/articles/${route.params.slug}`,
    {
        query: {
            include: ['blocks', 'products', 'read_also', 'calculator'].join(
                ','
            ),
        },
        getCachedData(key) {
            return nuxtApp.payload?.data?.[key]
        },
        useLastModified: true,
    }
)

if (error.value) {
    throw createError(
        Object.assign(error.value, {
            message: error.value.statusMessage,
            statusMessage: 'error',
        })
    )
}

if (data.value?.data) {
    modalStore.setFeedbackLabel(data.value.data?.feedback_label || '')
} else {
    modalStore.setFeedbackLabel('')
}

const blockTypes: { [key: number]: TBlockType } = {
    1: 'text',
    2: 'video',
    3: 'gallery',
    5: 'text + image',
}

const directionSlugByCalculatorName: { [key: string]: TDirectionSlug } = {
    'Продажа радиогидов': 'radioguide',
    'Комплекты аудиогидов': 'audioguide',
    'Кабины переводчиков': 'cabin',
    'Синхронный перевод': 'sinhroperevod',
    'Конференц-системы': 'conference-system',
}

const directionSlug = computed<TDirectionSlug | string>(() => {
    return data.value?.data.calculator?.data.name
        ? directionSlugByCalculatorName[data.value?.data.calculator?.data.name]
        : ''
})

function hasCalculator(
    directionSlug: TDirectionSlug | string
): directionSlug is TDirectionSlug {
    return directionSlug?.length > 0
}

const calculatorId = computed<number>(() => {
    switch (directionSlug.value) {
        case 'radioguide':
        case 'dostupsreda': {
            return 2
        }
        case 'audioguide': {
            return 1
        }
        case 'cabin': {
            return 4
        }
        case 'conference-system': {
            return 5
        }
        case 'sinhroperevod': {
            return 3
        }
        default: {
            return 0
        }
    }
})

const { data: calculatorData } = await useApiFetch<Page<ICalculator>>(
    `/v1/calculators/${calculatorId.value}`,
    {
        key: directionSlug,
        query: {
            include: [
                'charging_batteries',
                'type',
                'storage_bag',
                'headphones',
                'languages_translation_quantity',
                'cabin_type',
                'forced_ventilation',
                'case',
                'transportation_bag',
                'charging_batteries.product',
                'type.product',
                'storage_bag.product',
                'headphones.product',
                'languages_translation_quantity.product',
                'cabin_type.product',
                'forced_ventilation.product',
                'case.product',
                'transportation_bag.product',
            ].join(','),
        },
        watch: [() => calculatorId],
        lazy: true,
        getCachedData(key) {
            return nuxtApp.payload?.data?.[key]
        },
    }
)

const calculator = computed(() =>
    hasCalculator(directionSlug.value) && calculatorData.value?.data
        ? getCalculator(
              directionSlug.value === 'dostupsreda'
                  ? 'radioguide'
                  : directionSlug.value,
              calculatorData.value.data
          )
        : null
)

const breadcrumbs = computed<BreadcrumbItem[]>(() => [
    {
        label: 'Cromi',
        to: '/',
    },
    {
        label: `Блог PRO`,
        to: `/pro`,
    },
    {
        label: `${data.value?.data.name}`,
        to: `/pro/${data.value?.data.slug}`,
    },
])

if (data.value && data.value?.data && data.value?.data?.seo) {
    useSeoMeta({
        title: data.value.data.seo.seo_title,
        description: data.value.data.seo.seo_description,
        ogTitle: data.value.data.seo.og_title,
        ogDescription: data.value.data.seo.og_description,
        ogImage: data.value.data.seo.og_image
            ? data.value.data.seo.og_image
            : null,
    })

    useSchemaOrg([
        defineWebPage({
            '@type': 'ItemPage',
        }),
        defineArticle({
            '@type': 'BlogPosting',
            headline: data.value.data.name,
            name: data.value.data.name,
            articleBody: data.value.data.blocks.data
                .map((block) => {
                    return stripHtml(block.text) || ''
                })
                .join(' '),
            description: stripHtml(data.value.data.description),
            datePublished: data.value.data.published_at,
        }),
    ])
}
</script>
